import React, { useState, useMemo, useEffect } from 'react';
import { Users, Activity, Database, Plus, Trash2, Calculator, BarChart3, BookOpen, AlertTriangle, Info } from 'lucide-react';

// Dati iniziali delle 40 condizioni
const INITIAL_CONDITIONS = [
  { id: 1, name: 'ADHD', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 3, female: 1 }, '6-14': { male: 7, female: 3 } }, source: { reference: 'DSM-5', year: '2023', notes: 'Rapporto M:F 2-3:1', reliability: 'media' } },
  { id: 2, name: 'Allergia alle proteine del latte vaccino (APLV)', prevalence: { '0-2': { male: 3, female: 2.5 }, '2-6': { male: 1, female: 0.8 }, '6-14': { male: 0.3, female: 0.3 } }, source: { reference: 'SIGENP', year: '2021-2023', notes: 'Più frequente nel primo anno di vita, tende a risolversi', reliability: 'media' } },
  { id: 3, name: 'Allergie alimentari', prevalence: { '0-2': { male: 6, female: 5 }, '2-6': { male: 5, female: 4 }, '6-14': { male: 3, female: 3 } }, source: { reference: 'SIAIP', year: '2023', notes: 'Più frequenti nella prima infanzia, tendono a ridursi', reliability: 'media' } },
  { id: 4, name: 'Allergie respiratorie/Rinite allergica', prevalence: { '0-2': { male: 2, female: 1.5 }, '2-6': { male: 8, female: 7 }, '6-14': { male: 15, female: 14 } }, source: { reference: 'SIA', year: '2022', notes: 'Prevalenza crescente con età, spesso associata ad asma', reliability: 'alta' } },
  { id: 5, name: 'Ambliopia (occhio pigro)', prevalence: { '0-2': { male: 0.5, female: 0.5 }, '2-6': { male: 3, female: 3 }, '6-14': { male: 2, female: 2 } }, source: { reference: 'Screening visivi', year: '2021', notes: 'Prevalenza 2-4%', reliability: 'alta' } },
  { id: 6, name: 'Anemia sideropenica', prevalence: { '0-2': { male: 8, female: 7 }, '2-6': { male: 5, female: 6 }, '6-14': { male: 3, female: 8 } }, source: { reference: 'AIEOP', year: '2021-2023', notes: 'Più frequente in adolescenti femmine (menarca)', reliability: 'media' } },
  { id: 7, name: 'Ansia e disturbi ansiosi', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 2, female: 2 }, '6-14': { male: 5, female: 7 } }, source: { reference: 'SIPPed', year: '2023', notes: 'Più frequente femmine', reliability: 'media' } },
  { id: 8, name: 'Artrite idiopatica giovanile', prevalence: { '0-2': { male: 0.02, female: 0.03 }, '2-6': { male: 0.05, female: 0.07 }, '6-14': { male: 0.06, female: 0.08 } }, source: { reference: 'REUMAPED', year: '2023', notes: '50-90/100.000', reliability: 'alta' } },
  { id: 9, name: 'Asma bronchiale', prevalence: { '0-2': { male: 3, female: 2 }, '2-6': { male: 8, female: 6 }, '6-14': { male: 10, female: 8 } }, source: { reference: 'ISS - Sorveglianza PASSI', year: '2022', notes: 'Prevalenza crescente con età, lieve predominanza maschile', reliability: 'alta' } },
  { id: 10, name: 'Astigmatismo', prevalence: { '0-2': { male: 5, female: 5 }, '2-6': { male: 15, female: 15 }, '6-14': { male: 25, female: 25 } }, source: { reference: 'SIOPS-ISPOS', year: '2022', notes: 'Rilevato maggiormente con screening scolastici', reliability: 'alta' } },
  { id: 11, name: 'Celiachia', prevalence: { '0-2': { male: 0.5, female: 0.5 }, '2-6': { male: 1, female: 1.5 }, '6-14': { male: 1, female: 2 } }, source: { reference: 'Associazione Italiana Celiachia', year: '2022', notes: 'F:M = 2:1', reliability: 'alta' } },
  { id: 12, name: 'Coliche del lattante', prevalence: { '0-2': { male: 20, female: 18 }, '2-6': { male: 0, female: 0 }, '6-14': { male: 0, female: 0 } }, source: { reference: 'Linee guida SIP', year: '2022', notes: 'Tipiche dei primi 3-4 mesi di vita, autolimitanti', reliability: 'alta' } },
  { id: 13, name: 'Colite ulcerosa pediatrica', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 0.02, female: 0.02 }, '6-14': { male: 0.05, female: 0.05 } }, source: { reference: 'SIGENP', year: '2024', notes: 'Età media 10 anni', reliability: 'alta' } },
  { id: 14, name: 'Congiuntivite allergica', prevalence: { '0-2': { male: 2, female: 2 }, '2-6': { male: 8, female: 7 }, '6-14': { male: 15, female: 14 } }, source: { reference: 'SIOPS-ISPOS', year: '2022', notes: 'Spesso associata a rinite allergica', reliability: 'media' } },
  { id: 15, name: 'Criptorchidismo', prevalence: { '0-2': { male: 3, female: 0 }, '2-6': { male: 1, female: 0 }, '6-14': { male: 0.8, female: 0 } }, source: { reference: 'SIUP', year: '2021-2023', notes: 'Solo maschi', reliability: 'alta' } },
  { id: 16, name: 'Dermatite atopica (Eczema)', prevalence: { '0-2': { male: 15, female: 12 }, '2-6': { male: 10, female: 8 }, '6-14': { male: 5, female: 4 } }, source: { reference: 'SIDerP', year: '2022', notes: 'Esordio precoce, miglioramento con la crescita', reliability: 'alta' } },
  { id: 17, name: 'Dermatite da pannolino', prevalence: { '0-2': { male: 35, female: 32 }, '2-6': { male: 2, female: 2 }, '6-14': { male: 0, female: 0 } }, source: { reference: 'SIDerP', year: '2022', notes: 'Molto comune nei primi 2 anni, picco 9-12 mesi', reliability: 'alta' } },
  { id: 18, name: 'Dermatite seborroica', prevalence: { '0-2': { male: 12, female: 10 }, '2-6': { male: 3, female: 2 }, '6-14': { male: 5, female: 4 } }, source: { reference: 'SIDerP', year: '2021-2023', notes: 'Crosta lattea nei primi mesi, possibile recidiva in adolescenza', reliability: 'media' } },
  { id: 19, name: 'Diabete tipo 1', prevalence: { '0-2': { male: 0.05, female: 0.04 }, '2-6': { male: 0.12, female: 0.10 }, '6-14': { male: 0.15, female: 0.13 } }, source: { reference: 'SIEDP', year: '2024', notes: '12.26/100.000/anno', reliability: 'alta' } },
  { id: 20, name: 'Disturbi del linguaggio', prevalence: { '0-2': { male: 5, female: 3 }, '2-6': { male: 12, female: 8 }, '6-14': { male: 7, female: 5 } }, source: { reference: 'Studi logopedici pediatrici', year: '2022', notes: 'Più frequente nei maschi, spesso miglioramento con intervento', reliability: 'media' } },
  { id: 21, name: 'Disturbi dello spettro autistico', prevalence: { '0-2': { male: 0.5, female: 0.2 }, '2-6': { male: 1.5, female: 0.5 }, '6-14': { male: 1.8, female: 0.6 } }, source: { reference: 'ISS - Osservatorio Autismo', year: '2023', notes: 'Rapporto M:F 3-4:1, diagnosi in aumento', reliability: 'alta' } },
  { id: 22, name: 'DSA', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 2, female: 1 }, '6-14': { male: 5, female: 3 } }, source: { reference: 'Consensus DSA', year: '2022', notes: 'Diagnosi prevalentemente in età scolare, più frequente nei maschi', reliability: 'alta' } },
  { id: 23, name: 'Encopresi', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 3, female: 1 }, '6-14': { male: 2, female: 0.5 } }, source: { reference: 'SIGENP', year: '2022', notes: 'Più comune nei maschi', reliability: 'media' } },
  { id: 24, name: 'Enuresi notturna', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 15, female: 10 }, '6-14': { male: 8, female: 5 } }, source: { reference: 'Studi epidemiologici internazionali', year: '2023', notes: 'Più comune nei maschi', reliability: 'media' } },
  { id: 25, name: 'Epilessia', prevalence: { '0-2': { male: 0.3, female: 0.3 }, '2-6': { male: 0.5, female: 0.4 }, '6-14': { male: 0.6, female: 0.5 } }, source: { reference: 'LICE - Lega Italiana contro Epilessia', year: '2022', notes: 'Prevalenza stabile', reliability: 'alta' } },
  { id: 26, name: 'Fimosi', prevalence: { '0-2': { male: 50, female: 0 }, '2-6': { male: 10, female: 0 }, '6-14': { male: 1, female: 0 } }, source: { reference: 'Linee guida SIP urologiche', year: '2022', notes: 'Fisiologica fino a 3 anni, poi patologica', reliability: 'alta' } },
  { id: 27, name: 'Ipermetropia', prevalence: { '0-2': { male: 8, female: 8 }, '2-6': { male: 15, female: 15 }, '6-14': { male: 10, female: 10 } }, source: { reference: 'Screening visivi', year: '2021', notes: 'Fino 20% <6 anni', reliability: 'alta' } },
  { id: 28, name: 'Ipertensione arteriosa', prevalence: { '0-2': { male: 0.5, female: 0.5 }, '2-6': { male: 2, female: 1.5 }, '6-14': { male: 4.5, female: 3.5 } }, source: { reference: 'Ospedale Bambino Gesù', year: '2024', notes: 'Frequente associazione con obesità/sovrappeso', reliability: 'alta' } },
  { id: 29, name: 'Ipertrofia adenoidea', prevalence: { '0-2': { male: 10, female: 9 }, '2-6': { male: 25, female: 22 }, '6-14': { male: 15, female: 13 } }, source: { reference: 'SIOP', year: '2022', notes: 'Picco in età prescolare, causa frequente di OSAS pediatrica', reliability: 'media' } },
  { id: 30, name: 'Ipoacusia', prevalence: { '0-2': { male: 0.2, female: 0.2 }, '2-6': { male: 1, female: 1 }, '6-14': { male: 2, female: 2 } }, source: { reference: 'Screening uditivi neonatali', year: '2022', notes: 'Include ipoacusia congenita e acquisita', reliability: 'alta' } },
  { id: 31, name: 'Ipotiroidismo congenito', prevalence: { '0-2': { male: 0.03, female: 0.05 }, '2-6': { male: 0.03, female: 0.05 }, '6-14': { male: 0.03, female: 0.05 } }, source: { reference: 'ISS - Screening neonatale', year: '2023', notes: 'Prevalenza 1:2500-3000', reliability: 'alta' } },
  { id: 32, name: 'Malattia di Crohn pediatrica', prevalence: { '0-2': { male: 0.01, female: 0.01 }, '2-6': { male: 0.05, female: 0.05 }, '6-14': { male: 0.08, female: 0.08 } }, source: { reference: 'SIGENP', year: '2024', notes: 'Età media 12 anni', reliability: 'alta' } },
  { id: 33, name: 'Malocclusioni dentali', prevalence: { '0-2': { male: 5, female: 5 }, '2-6': { male: 20, female: 18 }, '6-14': { male: 35, female: 32 } }, source: { reference: 'SIOI', year: '2022', notes: 'Prevalenza crescente con età', reliability: 'media' } },
  { id: 34, name: 'Miopia', prevalence: { '0-2': { male: 1, female: 1 }, '2-6': { male: 4, female: 4 }, '6-14': { male: 15, female: 15 } }, source: { reference: 'Screening visivi', year: '2021', notes: '4% <6 anni', reliability: 'alta' } },
  { id: 35, name: 'Obesità', prevalence: { '0-2': { male: 2, female: 2 }, '2-6': { male: 8, female: 7 }, '6-14': { male: 12, female: 10 } }, source: { reference: 'OKkio alla Salute - ISS', year: '2023', notes: 'Variabilità regionale significativa, trend in aumento', reliability: 'alta' } },
  { id: 36, name: 'Orticaria cronica', prevalence: { '0-2': { male: 1, female: 1 }, '2-6': { male: 2, female: 2.5 }, '6-14': { male: 3, female: 4 } }, source: { reference: 'SIDerP', year: '2021-2023', notes: 'Lieve predominanza femminile, spesso idiopatica', reliability: 'media' } },
  { id: 37, name: 'OSAS (Apnee ostruttive del sonno)', prevalence: { '0-2': { male: 1, female: 0.8 }, '2-6': { male: 3, female: 2.5 }, '6-14': { male: 2, female: 1.8 } }, source: { reference: 'Studi Medicina pediatrica del sonno', year: '2022', notes: 'Spesso correlata a ipertrofia adenotonsillare', reliability: 'media' } },
  { id: 38, name: 'Otite media ricorrente', prevalence: { '0-2': { male: 25, female: 20 }, '2-6': { male: 15, female: 12 }, '6-14': { male: 5, female: 4 } }, source: { reference: 'SIOP', year: '2021-2023', notes: 'Picco nella prima infanzia, riduzione dopo 6 anni di età', reliability: 'media' } },
  { id: 39, name: 'Piede piatto', prevalence: { '0-2': { male: 45, female: 40 }, '2-6': { male: 25, female: 22 }, '6-14': { male: 15, female: 13 } }, source: { reference: 'SITOP', year: '2022', notes: 'Fisiologico nei primi anni, riduzione spontanea con crescita', reliability: 'media' } },
  { id: 40, name: 'Reflusso gastroesofageo', prevalence: { '0-2': { male: 40, female: 35 }, '2-6': { male: 10, female: 8 }, '6-14': { male: 5, female: 4 } }, source: { reference: 'SIGENP', year: '2023', notes: 'Fisiologico nel lattante, patologico se persistente', reliability: 'alta' } },
  { id: 41, name: 'Scoliosi', prevalence: { '0-2': { male: 0.1, female: 0.1 }, '2-6': { male: 0.5, female: 0.5 }, '6-14': { male: 2, female: 4 } }, source: { reference: 'Screening ortopedici scolastici', year: '2022', notes: 'Prevalenza aumenta in adolescenza, predominanza femminile', reliability: 'alta' } },
  { id: 42, name: 'Sindrome del colon irritabile', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 2, female: 2 }, '6-14': { male: 5, female: 7 } }, source: { reference: 'SIGENP', year: '2022', notes: 'Più frequente in età scolare e in adolescenza, predominanza femminile', reliability: 'media' } },
  { id: 43, name: 'Sindrome metabolica', prevalence: { '0-2': { male: 0.1, female: 0.1 }, '2-6': { male: 1, female: 0.8 }, '6-14': { male: 3, female: 2.5 } }, source: { reference: 'SIEDP', year: '2022', notes: 'Correlata a obesità, in aumento', reliability: 'media' } },
  { id: 44, name: 'Sovrappeso', prevalence: { '0-2': { male: 5, female: 4 }, '2-6': { male: 15, female: 13 }, '6-14': { male: 20, female: 18 } }, source: { reference: 'OKkio alla salute - ISS', year: '2023', notes: 'Distinto da obesità, prevalenza regionale variabile', reliability: 'alta' } },
  { id: 45, name: 'Stenosi del canale lacrimale', prevalence: { '0-2': { male: 6, female: 5 }, '2-6': { male: 1, female: 1 }, '6-14': { male: 0.2, female: 0.2 } }, source: { reference: 'SIOPS-ISPOS', year: '2022', notes: 'Tipica del primo anno di vita, spesso risoluzione spontanea', reliability: 'media' } },
  { id: 46, name: 'Stipsi cronica funzionale', prevalence: { '0-2': { male: 10, female: 9 }, '2-6': { male: 15, female: 13 }, '6-14': { male: 10, female: 12 } }, source: { reference: 'SIGENP', year: '2023', notes: 'Condizione funzionale molto comune, gestione dietetica', reliability: 'media' } },
  { id: 47, name: 'Strabismo', prevalence: { '0-2': { male: 1.5, female: 1.5 }, '2-6': { male: 3, female: 3 }, '6-14': { male: 3, female: 3 } }, source: { reference: 'SIOPS-ISPOS', year: '2024', notes: '~3%, diagnosi <6 anni', reliability: 'alta' } },
  { id: 48, name: 'Torcicollo miogeno congenito', prevalence: { '0-2': { male: 0.4, female: 0.3 }, '2-6': { male: 0.1, female: 0.1 }, '6-14': { male: 0, female: 0 } }, source: { reference: 'SITOP', year: '2021-2023', notes: 'Presente alla nascita, trattamento fisioterapico precoce', reliability: 'media' } },
  { id: 49, name: 'Valgismo delle ginocchia', prevalence: { '0-2': { male: 10, female: 9 }, '2-6': { male: 20, female: 18 }, '6-14': { male: 8, female: 10 } }, source: { reference: 'SITOP', year: '2022', notes: 'Fisiologico 2-7 anni, spesso autocorrettivo', reliability: 'media' } },
  { id: 50, name: 'Varicocele', prevalence: { '0-2': { male: 0, female: 0 }, '2-6': { male: 0, female: 0 }, '6-14': { male: 10, female: 0 } }, source: { reference: 'SIUP', year: '2022', notes: 'Maschi in adolescenza', reliability: 'media' } }
];

function App() {
  const STORAGE_KEY = 'pediatric-conditions-data';
  const STORAGE_PATIENT_KEY = 'pediatric-patient-data';
  const [conditions, setConditions] = useState(() => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? JSON.parse(saved) : INITIAL_CONDITIONS;
});
  const [patientData, setPatientData] = useState(() => {
  const saved = localStorage.getItem(STORAGE_PATIENT_KEY);
  return saved
    ? JSON.parse(saved)
    : {
        '0-2': { male: 100, female: 100 },
        '2-6': { male: 200, female: 200 },
        '6-14': { male: 200, female: 200 }
      };
});
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(conditions));
}, [conditions]);

useEffect(() => {
  localStorage.setItem(STORAGE_PATIENT_KEY, JSON.stringify(patientData));
}, [patientData]);

  const sortedConditions = useMemo(() => 
    [...conditions].sort((a, b) => a.name.localeCompare(b.name)),
    [conditions]
  );

  const calculateExpectedCases = (condition) => {
    const results = {};
    let totalCases = 0;

    Object.keys(patientData).forEach(ageGroup => {
      const malePatients = patientData[ageGroup].male;
      const femalePatients = patientData[ageGroup].female;
      const malePrevalence = condition.prevalence[ageGroup].male;
      const femalePrevalence = condition.prevalence[ageGroup].female;

      const maleCases = (malePatients * malePrevalence) / 100;
      const femaleCases = (femalePatients * femalePrevalence) / 100;

      results[ageGroup] = {
        male: maleCases.toFixed(1),
        female: femaleCases.toFixed(1),
        total: (maleCases + femaleCases).toFixed(1)
      };

      totalCases += maleCases + femaleCases;
    });

    return { byAge: results, total: totalCases.toFixed(1) };
  };

  const handleSaveCondition = (conditionData) => {
    if (editingCondition) {
      setConditions(conditions.map(c => 
        c.id === editingCondition.id ? { ...conditionData, id: c.id } : c
      ));
    } else {
      const newCondition = {
        ...conditionData,
        id: Date.now()
      };
      setConditions([...conditions, newCondition]);
    }
    setShowModal(false);
    setEditingCondition(null);
  };

  const handleDeleteCondition = (id) => {
    if (window.confirm('Sei sicuro di voler eliminare questa condizione?')) {
      setConditions(conditions.filter(c => c.id !== id));
      if (selectedCondition?.id === id) {
        setSelectedCondition(null);
      }
    }
  };

  const getAgeGroupLabel = (ageGroup) => {
    if (ageGroup === '0-2') return 'Prima infanzia (0-2 anni)';
    if (ageGroup === '2-6') return 'Seconda infanzia (2-6 anni)';
    return 'Terza infanzia (6-14 anni)';
  };

  const getReliabilityBadge = (reliability) => {
    const colors = {
      alta: 'bg-green-100 text-green-800 border-green-300',
      media: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      bassa: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[reliability] || colors.media;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-10 h-10 text-indigo-600" />
              <div>
                <h1 className="text-3xl font-bold text-indigo-900">
                  Calcolatore Prevalenza Malattia Pediatrica
                </h1>
                <p className="text-gray-600">50 condizioni morbose - Gestione fonti</p>
              </div>
            </div>
            <button
              onClick={() => setShowDisclaimer(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 border-2 border-orange-300"
            >
              <AlertTriangle className="w-5 h-5" />
              Disclaimer
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        {showDisclaimer && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-6 mb-6 rounded-lg shadow-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-orange-900 text-lg mb-2">⚠️ IMPORTANTE</h3>
                <div className="text-orange-800 space-y-2 text-sm">
                  <p className="font-semibold">Dati INDICATIVI a scopo DIMOSTRATIVO.</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Non sostituiscono fonti ufficiali</li>
                    <li>Verificare con letteratura aggiornata</li>
                    <li>Consultare: ISS, Ministero Salute, Società Scientifiche</li>
                  </ul>
                </div>
                <button
                  onClick={() => setShowDisclaimer(false)}
                  className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 font-semibold"
                >
                  Ho capito
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonna Assistiti */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Dati Assistiti</h2>
            </div>

            {Object.keys(patientData).map(ageGroup => (
              <div key={ageGroup} className="mb-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-3">
                  {getAgeGroupLabel(ageGroup)}
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 w-20">Maschi:</label>
                    <input
                      type="number"
                      value={patientData[ageGroup].male}
                      onChange={(e) => setPatientData({
                        ...patientData,
                        [ageGroup]: { ...patientData[ageGroup], male: parseInt(e.target.value) || 0 }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 w-20">Femmine:</label>
                    <input
                      type="number"
                      value={patientData[ageGroup].female}
                      onChange={(e) => setPatientData({
                        ...patientData,
                        [ageGroup]: { ...patientData[ageGroup], female: parseInt(e.target.value) || 0 }
                      })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="pt-2 border-t border-blue-200">
                    <span className="text-sm font-bold text-blue-900">
                      Totale: {patientData[ageGroup].male + patientData[ageGroup].female}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            <div className="mt-4 p-4 bg-indigo-100 rounded-lg border-2 border-indigo-300">
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-900">
                  {Object.values(patientData).reduce((sum, age) => sum + age.male + age.female, 0)}
                </div>
                <div className="text-sm text-indigo-700 font-semibold">Assistiti Totali</div>
              </div>
            </div>
          </div>

          {/* Colonna Condizioni */}
          <ConditionsColumn 
            sortedConditions={sortedConditions}
            selectedCondition={selectedCondition}
            setSelectedCondition={setSelectedCondition}
            setShowModal={setShowModal}
            setShowSourceModal={setShowSourceModal}
            setEditingCondition={setEditingCondition}
            handleDeleteCondition={handleDeleteCondition}
            getReliabilityBadge={getReliabilityBadge}
          />

          {/* Colonna Risultati */}
          <ResultsColumn 
            selectedCondition={selectedCondition}
            calculateExpectedCases={calculateExpectedCases}
            patientData={patientData}
            setShowSourceModal={setShowSourceModal}
            getAgeGroupLabel={getAgeGroupLabel}
          />
        </div>

        {/* Modali */}
        {showModal && (
          <ConditionModal
            condition={editingCondition}
            onSave={handleSaveCondition}
            onClose={() => {
              setShowModal(false);
              setEditingCondition(null);
            }}
            getAgeGroupLabel={getAgeGroupLabel}
          />
        )}

        {showSourceModal && selectedCondition && (
          <SourceModal
            condition={selectedCondition}
            onClose={() => setShowSourceModal(false)}
            getAgeGroupLabel={getAgeGroupLabel}
          />
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-1">
            Creato da Dario Ingletto · 2025
          </p>
          <p className="text-sm text-gray-600">
            Questo sito utilizza Google Analytics per migliorare il servizio
          </p>
        </div>
      </div>
    </div>
  );
}

// Componente Colonna Condizioni
const ConditionsColumn = ({ sortedConditions, selectedCondition, setSelectedCondition, setShowModal, setShowSourceModal, setEditingCondition, handleDeleteCondition, getReliabilityBadge }) => (
  <div className="bg-white rounded-lg shadow-lg p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Database className="w-6 h-6 text-green-600" />
        <h2 className="text-xl font-bold text-gray-900">Condizioni ({sortedConditions.length})</h2>
      </div>
      <button
        onClick={() => {
          setEditingCondition(null);
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
      >
        <Plus className="w-4 h-4" />
        Aggiungi
      </button>
    </div>

    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {sortedConditions.map(condition => (
        <div
          key={condition.id}
          className={`p-3 rounded-lg border-2 transition-all ${
            selectedCondition?.id === condition.id
              ? 'bg-green-100 border-green-500'
              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-start justify-between">
            <div
              onClick={() => setSelectedCondition(condition)}
              className="flex-1 cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="font-semibold text-gray-900 text-sm">{condition.name}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getReliabilityBadge(condition.source.reliability)}`}>
                  {condition.source.reliability}
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {condition.source.year}
              </div>
            </div>
            <div className="flex gap-2 ml-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCondition(condition);
                  setShowSourceModal(true);
                }}
                className="text-indigo-600 hover:text-indigo-800"
              >
                <BookOpen className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCondition(condition);
                  setShowModal(true);
                }}
                className="text-blue-600 hover:text-blue-800 text-lg"
              >
                ✏️
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteCondition(condition.id);
                }}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Componente Colonna Risultati
const ResultsColumn = ({ selectedCondition, calculateExpectedCases, patientData, setShowSourceModal, getAgeGroupLabel }) => (
  <div className="bg-white rounded-lg shadow-lg p-6">
    <div className="flex items-center gap-2 mb-4">
      <Calculator className="w-6 h-6 text-purple-600" />
      <h2 className="text-xl font-bold text-gray-900">Risultati</h2>
    </div>

    {selectedCondition ? (
      <div>
        <div className="bg-purple-100 p-4 rounded-lg mb-4 border-2 border-purple-300">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-purple-900 text-lg">
              {selectedCondition.name}
            </h3>
            <button
              onClick={() => setShowSourceModal(true)}
              className="text-indigo-600 hover:text-indigo-800"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>
          <div className="text-3xl font-bold text-purple-900 text-center my-2">
            {calculateExpectedCases(selectedCondition).total}
          </div>
          <div className="text-sm text-purple-700 text-center font-semibold">
            Casi attesi totali
          </div>
        </div>

        {Object.keys(patientData).map(ageGroup => {
          const results = calculateExpectedCases(selectedCondition).byAge[ageGroup];
          return (
            <div key={ageGroup} className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                {getAgeGroupLabel(ageGroup)}
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Maschi:</span>
                  <span className="font-bold text-blue-900">
                    {results.male} 
                    <span className="text-xs text-gray-600 ml-1">
                      ({selectedCondition.prevalence[ageGroup].male}%)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Femmine:</span>
                  <span className="font-bold text-pink-900">
                    {results.female}
                    <span className="text-xs text-gray-600 ml-1">
                      ({selectedCondition.prevalence[ageGroup].female}%)
                    </span>
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-semibold text-gray-900">Totale:</span>
                  <span className="font-bold text-purple-900">{results.total}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center text-gray-500 py-8">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <p>Seleziona una condizione</p>
      </div>
    )}
  </div>
);

// Componente Modal Condizione
const ConditionModal = ({ condition, onSave, onClose, getAgeGroupLabel }) => {
  const [formData, setFormData] = useState(
    condition || {
      name: '',
      prevalence: {
        '0-2': { male: 0, female: 0 },
        '2-6': { male: 0, female: 0 },
        '6-14': { male: 0, female: 0 }
      },
      source: {
        reference: '',
        year: new Date().getFullYear().toString(),
        notes: '',
        reliability: 'media'
      }
    }
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {condition ? 'Modifica Condizione' : 'Nuova Condizione'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Condizione *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                placeholder="Es: Diabete tipo 1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fonte/Riferimento *
                </label>
                <input
                  type="text"
                  value={formData.source.reference}
                  onChange={(e) => setFormData({
                    ...formData,
                    source: { ...formData.source, reference: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="Es: ISS"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anno *
                </label>
                <input
                  type="text"
                  value={formData.source.year}
                  onChange={(e) => setFormData({
                    ...formData,
                    source: { ...formData.source, year: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="2024"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Affidabilità
              </label>
              <select
                value={formData.source.reliability}
                onChange={(e) => setFormData({
                  ...formData,
                  source: { ...formData.source, reliability: e.target.value }
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              >
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="bassa">Bassa</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note metodologiche
              </label>
              <textarea
                value={formData.source.notes}
                onChange={(e) => setFormData({
                  ...formData,
                  source: { ...formData.source, notes: e.target.value }
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="Note sulla fonte..."
              />
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Prevalenza per fascia (%)</h3>
              
              {Object.keys(formData.prevalence).map(ageGroup => (
                <div key={ageGroup} className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">
                    {getAgeGroupLabel(ageGroup)}
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Maschi (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.prevalence[ageGroup].male}
                        onChange={(e) => setFormData({
                          ...formData,
                          prevalence: {
                            ...formData.prevalence,
                            [ageGroup]: {
                              ...formData.prevalence[ageGroup],
                              male: parseFloat(e.target.value) || 0
                            }
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Femmine (%)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.prevalence[ageGroup].female}
                        onChange={(e) => setFormData({
                          ...formData,
                          prevalence: {
                            ...formData.prevalence,
                            [ageGroup]: {
                              ...formData.prevalence[ageGroup],
                              female: parseFloat(e.target.value) || 0
                            }
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  if (formData.name.trim() && formData.source.reference.trim()) {
                    onSave(formData);
                  } else {
                    alert('Inserisci almeno nome e fonte');
                  }
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-semibold"
              >
                Salva
              </button>
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 font-semibold"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente Modal Fonte
const SourceModal = ({ condition, onClose, getAgeGroupLabel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-indigo-600" />
            <h2 className="text-2xl font-bold text-gray-900">Informazioni Fonte</h2>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">{condition.name}</h3>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${
                  condition.source.reliability === 'alta' ? 'bg-green-100 text-green-800 border-green-300' :
                  condition.source.reliability === 'media' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                  'bg-red-100 text-red-800 border-red-300'
                }`}>
                  Affidabilità: {condition.source.reliability}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Riferimento:</label>
                  <p className="text-gray-900">{condition.source.reference}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Anno:</label>
                  <p className="text-gray-900">{condition.source.year}</p>
                </div>
              </div>
              
              {condition.source.notes && (
                <div className="mt-4">
                  <label className="text-sm font-semibold text-gray-700">Note:</label>
                  <p className="text-gray-700 text-sm mt-1">{condition.source.notes}</p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-3">Dati di Prevalenza (%)</h4>
              <div className="space-y-3">
                {Object.keys(condition.prevalence).map(ageGroup => (
                  <div key={ageGroup} className="bg-white p-3 rounded-md">
                    <div className="font-medium text-gray-800 mb-2">
                      {getAgeGroupLabel(ageGroup)}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Maschi: </span>
                        <span className="font-bold text-blue-900">{condition.prevalence[ageGroup].male}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Femmine: </span>
                        <span className="font-bold text-pink-900">{condition.prevalence[ageGroup].female}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-900">
                  <p className="font-semibold mb-1">Attenzione:</p>
                  <p>Verificare sempre la fonte originale. I dati possono variare.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-semibold"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;